import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { SignInFormInputs } from "./SignInFormInputs";
import { AuthSubmitButton } from "./AuthSubmitButton";
import { AuthFormError } from "./AuthFormError";
import { AuthFormActions } from "./AuthFormActions";
import { useAuthForm } from "@/hooks/use-auth-form";
import { useToast } from "@/hooks/use-toast";
import { useCaptchaEnabled } from "@/hooks/useCaptchaEnabled";
import { useTracking } from "@/hooks/useInteractionTracking";
import { AuthError, AuthApiError } from "@supabase/supabase-js";

type SignInFormProps = {
  onToggleMode: () => void;
  onSuccess?: () => Promise<void>;
  onForgotPassword?: () => void;
};

const SignInForm = ({
  onToggleMode,
  onSuccess,
  onForgotPassword,
}: SignInFormProps) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    phone: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const { handleSignIn } = useAuthForm();
  const { toast } = useToast();
  const { trackFeatureUsage } = useTracking();
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  
  // Check if CAPTCHA is enabled by admin
  const { isCaptchaEnabled, isLoading: isCaptchaSettingLoading } = useCaptchaEnabled();

  useEffect(() => {
    const hash = searchParams.get("type");
    if (hash === "recovery") {
      toast({
        title: "Password Reset Email Sent",
        description: "Check your email for the password reset link",
      });
    }
  }, [searchParams, toast]);

  useEffect(() => {
    // Skip CAPTCHA if disabled by admin or still loading setting
    if (!isCaptchaEnabled || isCaptchaSettingLoading) {
      console.log('⏭️ CAPTCHA is disabled by admin, skipping widget render');
      return;
    }
    
    if (!turnstileRef.current || widgetIdRef.current) return;
    
    const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

    if (!siteKey) {
      console.error('❌ VITE_TURNSTILE_SITE_KEY is not defined');
      setError('CAPTCHA configuration error. Please contact support.');
      return;
    }
    
    console.log('🔄 Waiting for Turnstile to load...');
    
    let attempts = 0;
    const maxAttempts = 50;
    
    const checkTurnstile = setInterval(() => {
      attempts++;
      
      if (window.turnstile) {
        clearInterval(checkTurnstile);
        console.log('✅ Turnstile loaded, rendering widget...');
        
        try {
          widgetIdRef.current = window.turnstile.render(turnstileRef.current!, {
            sitekey: siteKey,
            callback: (token: string) => {
              console.log('✅ CAPTCHA completed');
              setCaptchaToken(token);
            },
          });
          console.log('✅ Widget rendered successfully');
        } catch (err) {
          console.error('❌ Failed to render Turnstile widget:', err);
          setError('CAPTCHA failed to load. Please refresh the page.');
        }
      } else if (attempts >= maxAttempts) {
        clearInterval(checkTurnstile);
        console.error('❌ Turnstile script failed to load after 5 seconds');
        setError('CAPTCHA failed to load. Please refresh the page.');
      }
    }, 100);

    return () => {
      clearInterval(checkTurnstile);
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
  }, [isCaptchaEnabled, isCaptchaSettingLoading]);

  const getErrorMessage = (error: AuthError) => {
    if (error instanceof AuthApiError) {
      switch (error.status) {
        case 400:
          if (error.message.includes("Invalid login credentials")) {
            return "Invalid email or password. Please check your credentials and try again.";
          }
          if (error.message.includes("Email not confirmed")) {
            return "Please verify your email address before signing in.";
          }
          return "Invalid request. Please check your input and try again.";
        case 422:
          return "Email already registered with an existing account. If you joined a Nimi social last year, please choose 'Sign in' below, then 'Forgot password' to reset your account.";
        case 429:
          return "Too many attempts. Please try again later.";
        case 404:
          return "Account not found. Please check your credentials or sign up.";
        default:
          return error.message;
      }
    }
    return "An unexpected error occurred. Please try again.";
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidForm = () => {
    const { email, password } = formData;

    if (!email || !password) {
      setError("Please fill in all required fields");
      return false;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return false;
    }

    // Only require CAPTCHA if enabled by admin
    if (isCaptchaEnabled && !captchaToken) {
      setError("Please complete the CAPTCHA verification");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || !isValidForm()) return;
    setLoading(true);
    setError(null);
    
    // Track sign-in attempt
    trackFeatureUsage("auth", "sign_in_attempt", { email: formData.email });
    
    try {
      await handleSignIn(formData.email, formData.password, captchaToken ?? undefined);
      
      // Track successful sign-in
      trackFeatureUsage("auth", "sign_in_success", { email: formData.email });
      
      if (onSuccess) {
        await onSuccess();
      }
    } catch (err) {
      console.error("Authentication error:", err);
      const error = err as AuthError;
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      
      // Track failed sign-in
      trackFeatureUsage("auth", "sign_in_failed", { email: formData.email, error: errorMessage });
      
      toast({
        variant: "destructive",
        title: "Authentication Error",
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <AuthFormError error={error} />
      <SignInFormInputs
        formData={formData}
        setFormData={setFormData}
      />
      {/* Only show CAPTCHA widget if enabled */}
      {isCaptchaEnabled && <div ref={turnstileRef} />}
      <div className="space-y-4">
        <AuthSubmitButton loading={loading} isSignUp={false} />
        <AuthFormActions
          isSignUp={false}
          onToggleMode={onToggleMode}
          onForgotPassword={onForgotPassword}
        />
      </div>
    </form>
  );
};

export default SignInForm;
